import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ChevronLeft,
  Clock,
  CornerDownRight,
  ExternalLink,
  Facebook,
  Instagram,
  Loader2,
  MessageCircle,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatInTz, propertyTimezone } from "@/lib/timezone";
import CommentThread from "@/components/CommentThread";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/**
 * Social Comments — a two-pane workspace for reader engagement.
 *
 * LEFT PANE: every recent post across every connected FB Page + IG
 * business account, with a comment-count badge. Newest-first, mixed
 * platforms.
 *
 * RIGHT PANE: the comment tree for whichever post the editor has
 * selected. Each comment (including nested replies) gets an "AR"
 * button that pops open the Assisted Response drawer.
 *
 * AR DRAWER: an editor writes an optional angle, hits Generate → the
 * LLM returns 3 candidate replies (drafted using the property's
 * comment-rules for that platform) → editor picks one, edits freely,
 * publishes to Meta. Nothing goes live without an explicit publish
 * click.
 */

function PlatformIcon({ platform, className = "w-3.5 h-3.5" }) {
  return platform === "facebook" ? (
    <Facebook className={`${className} text-[#1877F2]`} />
  ) : (
    <Instagram className={`${className} text-fuchsia-600`} />
  );
}

function ARDrawer({ target, onClose, tz }) {
  const qc = useQueryClient();
  const [angle, setAngle] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [editable, setEditable] = useState(["", "", ""]);

  const platform = target.postCtx.platform;
  // Rebuild thread context so the LLM understands what's being replied to.
  const threadContext = useMemo(() => {
    if (!target.parentChain?.length) return "";
    return target.parentChain
      .map((c) => `${c.author_name || "user"}: ${c.text}`)
      .join("\n");
  }, [target.parentChain]);

  const gen = useMutation({
    mutationFn: () =>
      api.generateCommentReplies({
        platform,
        post_text: target.postText || "",
        comment_text: target.comment.text,
        thread_context: threadContext,
        angle: angle.trim(),
      }),
    onSuccess: (r) => {
      const replies = r.replies || ["", "", ""];
      setEditable(replies);
      setSelectedIdx(0);
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail || tr("Could not generate replies — try again."),
      ),
  });

  const publish = useMutation({
    mutationFn: () =>
      api.replyToSocialComment(target.comment.id, {
        platform,
        page_id:
          platform === "facebook" ? target.postCtx.page_id : undefined,
        ig_user_id:
          platform === "instagram" ? target.postCtx.ig_user_id : undefined,
        message: editable[selectedIdx].trim(),
      }),
    onSuccess: () => {
      toast.success(tr("Reply published — refreshing the thread…"));
      qc.invalidateQueries({
        queryKey: ["social-comments", target.postCtx.post_id],
      });
      qc.invalidateQueries({ queryKey: ["social-posts"] });
      onClose();
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail || tr("Publish failed — retry from Meta directly if this persists."),
      ),
  });

  const chosen = (editable[selectedIdx] || "").trim();
  const canPublish = chosen.length > 0 && !publish.isPending;

  return (
    <div
      data-testid="ar-drawer"
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl bg-white border border-[var(--border)] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white border-b border-[var(--border)] px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PlatformIcon platform={platform} />
            {trRich("<0>Assisted Response</0>", null, [<span className="overline" />])}
          </div>
          <button
            data-testid="ar-close"
            onClick={onClose}
            className="text-[var(--ink-muted)] hover:text-[var(--ink)] text-sm"
          >{tr("Close")}</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Original comment card */}
          <div className="border border-[var(--border)] bg-[var(--secondary)] p-3 text-sm">
            <div className="overline mb-1">{tr("Replying to")}</div>
            <div className="text-[var(--ink)]">
              <strong>
                {target.comment.author_name ||
                  (platform === "instagram" ? tr("Instagram user") : tr("Facebook user"))}
                :
              </strong>{" "}
              {target.comment.text}
            </div>
            {threadContext && (
              <details className="mt-2">
                <summary className="text-[11px] text-[var(--ink-muted)] cursor-pointer">{tr("Show thread context ({parentChainCount} parent comment{s})", { parentChainCount: target.parentChain.length, s: target.parentChain.length === 1 ? "" : "s" })}</summary>
                <pre className="text-[11px] whitespace-pre-wrap font-mono-display text-[var(--ink-muted)] mt-1">
                  {threadContext}
                </pre>
              </details>
            )}
          </div>

          {/* Angle input */}
          <div>
            <label className="overline mb-1.5 block">{trRich("Your angle <0>(optional — steer the LLM toward a specific answer)</0>", null, [<span className="text-[var(--ink-muted)] normal-case tracking-normal" />])}
            </label>
            <textarea
              data-testid="ar-angle"
              value={angle}
              onChange={(e) => setAngle(e.target.value)}
              placeholder={tr("e.g. Answer the date question and invite them to follow us for updates")}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)] resize-y"
            />
          </div>

          <button
            data-testid="ar-generate"
            onClick={() => gen.mutate()}
            disabled={gen.isPending}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-[var(--slate-900)] bg-[var(--slate-900)] text-white hover:bg-[var(--ink)] disabled:opacity-60"
          >
            {gen.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />{" "}{tr("Generating…")}</>
            ) : gen.isSuccess ? (
              <>
                <RefreshCw className="w-4 h-4" />{" "}{tr("Regenerate 3 replies")}</>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />{" "}{tr("Generate 3 replies")}</>
            )}
          </button>

          {/* Reply candidates */}
          {gen.isSuccess && (
            <div className="space-y-3 pt-2 border-t border-[var(--border)]">
              <div className="overline">{tr("Pick one & edit before publishing")}</div>
              {editable.map((txt, i) => {
                const selected = selectedIdx === i;
                return (
                  <label
                    key={i}
                    data-testid={`ar-option-${i}`}
                    className={
                      "block border p-3 cursor-pointer " +
                      (selected
                        ? "border-[var(--accent)] bg-orange-50/40"
                        : "border-[var(--border)] hover:border-[var(--ink)]")
                    }
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <input
                        type="radio"
                        name="ar-choice"
                        checked={selected}
                        onChange={() => setSelectedIdx(i)}
                        className="accent-[var(--accent)]"
                      />
                      {trRich("<0>Option {value}</0>", { value: i + 1 }, [<span className="overline" />])}
                    </div>
                    <textarea
                      data-testid={`ar-textarea-${i}`}
                      value={txt}
                      onFocus={() => setSelectedIdx(i)}
                      onChange={(e) => {
                        const next = editable.slice();
                        next[i] = e.target.value;
                        setEditable(next);
                      }}
                      rows={3}
                      placeholder={txt ? "" : tr("(empty — the LLM chose to skip this option, likely because the comment is spam or hate speech)")}
                      className="w-full px-2 py-1.5 text-sm font-serif-display border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)] resize-y"
                    />
                  </label>
                );
              })}
              <button
                data-testid="ar-publish"
                onClick={() => publish.mutate()}
                disabled={!canPublish}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-60"
              >
                {publish.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />{tr("Publishing to {value}…", { value: platform === "facebook" ? tr("Facebook") : tr("Instagram") })}</>
                ) : (
                  <>
                    <Send className="w-4 h-4" />{tr("Publish Option {value} to {value2}", { value: selectedIdx + 1, value2: platform === "facebook" ? tr("Facebook") : tr("Instagram") })}
                  </>
                )}
              </button>
              <p className="text-[11px] text-[var(--ink-muted)]">{trRich("Nothing publishes until you click above. Rules from <0>Settings · Community management</0> shape the drafts.", null, [<a
                  href="/settings"
                  className="underline hover:text-[var(--ink)]" />])}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * RECENT tab — flat feed of the most recent comments across every
 * connected FB Page + IG account, newest-first. Each row shows the
 * commenter, the comment text, and the parent post's title so the
 * editor can triage the whole newsroom's inbound activity in one
 * scrolling pane instead of clicking into 15 posts one at a time.
 *
 * Two per-row actions:
 *   - AR: pop the Assisted Response drawer for that comment.
 *   - View thread: jump into the two-pane view with the parent post
 *     pre-selected so the editor can see the full context.
 */
/** Case- and accent-insensitive fold — mirrors the backend's `fold`.
 *  Readers comment in Spanish and English on the same posts, so
 *  searching "informacion" has to match "información" too. */
function fold(s) {
  return (s || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function RecentCommentsFeed({
  data,
  isLoading,
  onOpenAR,
  onOpenPost,
  tz,
  query = "",
  unansweredOnly = false,
  emptyLabel,
}) {
  if (isLoading) {
    return (
      <div className="border border-[var(--border)] bg-white p-6 text-sm text-[var(--ink-muted)]">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />{tr("Loading recent comments…")}</div>
    );
  }
  const allItems = data?.items || [];
  const errors = data?.errors || [];
  // Filtering happens here, over the payload React Query already has
  // cached. A search that re-hit Meta on every keystroke would burn
  // through the Graph rate limit in seconds and feel laggy besides.
  const q = fold(query);
  const items = allItems.filter(({ comment, post }) => {
    if (unansweredOnly && comment.needs_reply === false) return false;
    if (!q) return true;
    return (
      fold(`${comment.text || ""} ${comment.author_name || ""}`).includes(q) ||
      fold(post.title).includes(q)
    );
  });
  if (items.length === 0) {
    return (
      <div className="border border-[var(--border)] bg-white p-6 text-sm text-[var(--ink-muted)]">
        {emptyLabel ||
          tr("No recent comments across any connected Facebook Page or Instagram account. Comments appear here as soon as readers start engaging.")}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {errors.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 p-3 text-xs">
          <div className="overline mb-1 text-amber-900">{tr("Some sources returned errors")}</div>
          <ul className="list-disc list-inside space-y-0.5 text-amber-900">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      {(q || unansweredOnly) && (
        <div
          data-testid="recent-result-count"
          className="text-[11px] font-mono-display uppercase tracking-wider text-[var(--ink-muted)]"
        >
          {tr("{itemCount} of {allItemCount} comment{s}", { itemCount: items.length, allItemCount: allItems.length, s: allItems.length === 1 ? "" : "s" })}
        </div>
      )}
      <ul
        data-testid="recent-comments-feed"
        className="border border-[var(--border)] bg-white divide-y divide-[var(--border)]"
      >
        {items.map(({ comment, post }) => {
          const fallback =
            post.platform === "instagram" ? tr("Instagram user") : tr("Facebook user");
          // Long post titles crowd out the comment. Keep the headline
          // (the actual comment) as the visual anchor of each row and
          // let the parent post reference sit in a small caption line.
          const postTitle = post.title || tr("(untitled post)");
          const truncatedTitle =
            postTitle.length > 60
              ? postTitle.slice(0, 57).trimEnd() + "…"
              : postTitle;
          return (
            <li
              key={`${post.post_id}-${comment.id}`}
              data-testid={`recent-comment-${comment.id}`}
              className="px-4 py-4 hover:bg-[var(--secondary)]/40"
            >
              <div className="flex items-start gap-3">
                <PlatformIcon
                  platform={post.platform}
                  className="w-3.5 h-3.5 mt-1 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  {/* A flattened reply with no context reads as a
                      non-sequitur ("yes please!" — to what?). Show what
                      it answers so the row stands on its own. */}
                  {comment.is_reply && (
                    <div
                      data-testid={`recent-reply-context-${comment.id}`}
                      className="mb-1 flex items-start gap-1 text-[11px] text-[var(--ink-muted)] min-w-0"
                    >
                      <CornerDownRight className="w-3 h-3 shrink-0 mt-0.5" />
                      {trRich("<0>replying to <1>{reply_to_name}</1>{value}</0>", { reply_to_name: comment.reply_to_name || fallback, value: comment.reply_to_text
                          ? `: "${comment.reply_to_text}"`
                          : "" }, [<span className="truncate" />, <span className="font-medium" />])}
                    </div>
                  )}
                  {/* Comment text — the visual centre of the row.
                      This is what the editor actually wants to read. */}
                  <div
                    data-testid={`recent-comment-text-${comment.id}`}
                    className="text-[15px] leading-snug text-[var(--ink)] whitespace-pre-wrap break-words"
                  >
                    {comment.text || (
                      <span className="italic text-[var(--ink-muted)]">{tr("(no text — reaction only)")}</span>
                    )}
                  </div>
                  {/* Author + timestamp + likes — secondary meta. */}
                  <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                    <span className="text-xs font-medium text-[var(--ink-muted)]">
                      {comment.author_name || fallback}
                    </span>
                    <span className="text-[10px] font-mono-display text-[var(--ink-muted)]">
                      {comment.created_time
                        ? formatInTz(comment.created_time, tz, "compact")
                        : ""}
                    </span>
                    {comment.like_count > 0 && (
                      <span className="text-[10px] text-[var(--ink-muted)]">{tr("· {like_count} like{s}", { like_count: comment.like_count, s: comment.like_count === 1 ? "" : "s" })}
                      </span>
                    )}
                    {comment.is_page_author && (
                      <span className="px-1 py-0 text-[9px] uppercase tracking-wider font-mono-display bg-[var(--accent)] text-white">{tr("Your Page")}</span>
                    )}
                    {comment.needs_reply && (
                      <span className="px-1 py-0 text-[9px] uppercase tracking-wider font-mono-display border border-[var(--accent)] text-[var(--accent-hover)]">{tr("Needs reply")}</span>
                    )}
                  </div>
                  {/* Post context — truncated so it never crowds the
                      comment. Full title still available via View
                      thread / Open on FB. */}
                  <div className="mt-1.5 text-[11px] text-[var(--ink-muted)] flex items-center gap-2 flex-wrap">
                    {trRich("<0>on “{truncatedTitle}”</0>", { truncatedTitle }, [<span
                      className="uppercase tracking-wider font-mono-display"
                      title={postTitle} />])}
                    <button
                      data-testid={`recent-view-thread-${comment.id}`}
                      onClick={() =>
                        onOpenPost({
                          platform: post.platform,
                          post_id: post.post_id,
                          page_id: post.page_id,
                          ig_user_id: post.ig_user_id,
                          handle: post.handle,
                          title: post.title,
                          permalink: post.permalink,
                          comment_count: 0,
                        })
                      }
                      className="inline-flex items-center gap-0.5 underline hover:text-[var(--ink)]"
                    >{tr("View thread")}</button>
                    {post.permalink && (
                      <a
                        href={post.permalink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-0.5 hover:text-[var(--ink)]"
                      >{tr("Open on {s}", { s: post.platform === "facebook" ? tr("FB") : tr("IG") })}
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`recent-ar-${comment.id}`}
                  onClick={() =>
                    onOpenAR({
                      comment,
                      postText: post.title,
                      postCtx: {
                        platform: post.platform,
                        post_id: post.post_id,
                        page_id: post.page_id,
                        ig_user_id: post.ig_user_id,
                      },
                      parentChain: [],
                    })
                  }
                  title={tr("Assisted Response — let the LLM draft 3 candidate replies")}
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-mono-display border border-[var(--accent)] text-[var(--accent-hover)] hover:bg-orange-50"
                >
                  <Sparkles className="w-3 h-3" />{tr("AR")}</button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function SocialCommentsPage() {
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
    staleTime: 5 * 60 * 1000,
  });
  const tz = propertyTimezone(settings);

  const { data: postsData, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["social-posts"],
    queryFn: api.listSocialPosts,
    staleTime: 60 * 1000,
  });
  const posts = postsData?.items || [];
  const errors = postsData?.errors || [];
  const needsReconnectFb = !!postsData?.needs_reconnect_fb;

  const [selectedPost, setSelectedPost] = useState(null);
  const [platformFilter, setPlatformFilter] = useState("all");
  const [arTarget, setArTarget] = useState(null);
  const [postQuery, setPostQuery] = useState("");
  const [commentQuery, setCommentQuery] = useState("");
  const [unansweredOnly, setUnansweredOnly] = useState(false);

  // Recent feed is a separate, heavier endpoint (fan-out over every
  // post that has comments). Only fire it when the tab is active so
  // switching to All/Facebook/Instagram stays snappy.
  const recentQuery = useQuery({
    queryKey: ["social-recent-comments", "recent"],
    queryFn: () => api.listRecentSocialComments(),
    enabled: platformFilter === "recent",
    staleTime: 60 * 1000,
  });

  // Search pulls a deliberately WIDER corpus than Recent — 25 posts per
  // connected account instead of 10, 300 rows instead of 40 — then
  // filters it in the browser. One fan-out per minute, instant results
  // per keystroke. The trade-off is honest and stated in the UI: this
  // searches recent activity, not the Page's entire comment history,
  // because Meta's Graph API has no cross-post comment search edge.
  const searchQuery = useQuery({
    queryKey: ["social-recent-comments", "search"],
    queryFn: () =>
      api.listRecentSocialComments({ posts_per_source: 25, limit: 300 }),
    enabled: platformFilter === "search",
    staleTime: 60 * 1000,
  });

  const commentsQuery = useQuery({
    queryKey: ["social-comments", selectedPost?.post_id],
    enabled: !!selectedPost,
    queryFn: () =>
      api.getSocialPostComments(selectedPost.post_id, {
        platform: selectedPost.platform,
        page_id:
          selectedPost.platform === "facebook"
            ? selectedPost.page_id
            : undefined,
        ig_user_id:
          selectedPost.platform === "instagram"
            ? selectedPost.ig_user_id
            : undefined,
      }),
  });

  // Only meaningful once a Facebook post is open — it's derived from
  // the comments actually returned, not from the permission list.
  const identity = commentsQuery.data?.identity;
  const identityGapFb =
    selectedPost?.platform === "facebook" &&
    !needsReconnectFb &&
    identity &&
    identity.total > 0 &&
    identity.named === 0
      ? identity
      : null;

  const filteredPosts = (() => {
    const base =
      platformFilter === "facebook" || platformFilter === "instagram"
        ? posts.filter((p) => p.platform === platformFilter)
        : posts;
    const q = fold(postQuery);
    if (!q) return base;
    return base.filter(
      (p) => fold(p.title).includes(q) || fold(p.handle).includes(q),
    );
  })();

  return (
    // Same reason as UploadArticlePage — the App shell wraps children
    // in `overflow-hidden`, so this page needs its own scroll container
    // for the post list + comments pane + AR drawer to be reachable on
    // shorter screens.
    <div className="flex-1 overflow-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
      <div className="mb-6">
        <div className="overline mb-2">{tr("Community")}</div>
        <div className="flex items-baseline gap-4 flex-wrap">
          <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight">{tr("Social Comments")}</h1>
          <button
            data-testid="social-refresh"
            onClick={() => {
              refetch();
              if (platformFilter === "recent") recentQuery.refetch();
              if (platformFilter === "search") searchQuery.refetch();
            }}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[var(--ink-muted)] hover:text-[var(--ink)] border border-[var(--border)]"
          >
            <RefreshCw
              className={`w-3 h-3 ${
                isFetching || recentQuery.isFetching || searchQuery.isFetching
                  ? "animate-spin"
                  : ""
              }`}
            />{tr("Refresh")}</button>
        </div>
        <p className="mt-3 text-sm text-[var(--ink-muted)] max-w-2xl">{tr("Every recent post across your connected Facebook Pages and Instagram business accounts, with comment threads and AI-drafted replies you approve before publishing.")}</p>
      </div>

      {needsReconnectFb && (
        <div
          data-testid="reconnect-fb-banner"
          className="mb-4 border border-amber-300 bg-amber-50 p-3 text-xs"
        >
          <div className="overline mb-1 text-amber-900">{tr("Reconnect Facebook to see commenter names")}</div>
          <p className="text-amber-900 leading-relaxed">{trRich("Facebook requires a new permission (<0>pages_read_user_content</0>) to expose the name of the person who left a comment. Your current connection was made before that permission was requested, so every Facebook commenter is shown as “Facebook user”. <1>Reconnect this property in Settings</1> to restore names. Instagram usernames are unaffected.", null, [<code className="font-mono-display" />, <a
              href="/settings"
              className="underline font-medium hover:text-amber-950" />])}</p>
        </div>
      )}

      {/* Second, DIFFERENT failure mode for commenter names. The banner
          above fires when the OAuth grant is missing
          `pages_read_user_content` — a reconnect fixes it. This one
          fires when the scope IS granted and Meta is still redacting
          every reader, which reconnecting will never fix: the app needs
          Advanced Access to that permission via App Review. Until now
          both cases looked identical ("Facebook user" on every row) and
          this one showed no explanation at all. */}
      {identityGapFb && (
        <div
          data-testid="identity-gap-banner"
          className="mb-4 border border-amber-300 bg-amber-50 p-3 text-xs"
        >
          <div className="overline mb-1 text-amber-900">{tr("Facebook is withholding commenter names")}</div>
          <p className="text-amber-900 leading-relaxed">{trRich("The <0>pages_read_user_content</0> permission is granted, but Meta still returned no name on any of the {total} reader comment{s} on this post. That means the app is on Standard Access for that permission — names are only revealed to apps with Advanced Access, which is granted through Meta App Review in the app dashboard. Reconnecting the page will not change this. Replies still publish normally, and Instagram usernames are unaffected.", { total: identityGapFb.total, s: identityGapFb.total === 1 ? "" : "s" }, [<code className="font-mono-display" />])}</p>
        </div>
      )}

      {errors.length > 0 && (
        <div className="mb-4 border border-amber-200 bg-amber-50 p-3 text-xs">
          <div className="overline mb-1 text-amber-900">{tr("Some connections returned errors")}</div>
          <ul className="list-disc list-inside space-y-0.5 text-amber-900">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Platform filter — hidden on mobile when a post is selected so
          the comments pane gets the full screen (the filter operates on
          the list view, not the comments view). */}
      <div
        className={
          "mb-4 items-center gap-2 flex-wrap " +
          (selectedPost ? "hidden lg:flex" : "flex")
        }
      >
        {[
          { k: "all", label: tr("All") },
          { k: "facebook", label: tr("Facebook") },
          { k: "instagram", label: tr("Instagram") },
          { k: "recent", label: tr("Recent") },
          { k: "search", label: tr("Search") },
        ].map(({ k, label }) => (
          <button
            key={k}
            data-testid={`filter-${k}`}
            onClick={() => {
              setPlatformFilter(k);
              // Recent and Search are flat feed views — don't leave a
              // post selection floating from the two-pane mode.
              if (k === "recent" || k === "search") setSelectedPost(null);
            }}
            className={
              "inline-flex items-center gap-1.5 px-3 py-1 text-xs uppercase tracking-wider border " +
              (platformFilter === k
                ? "bg-[var(--slate-900)] text-white border-[var(--slate-900)]"
                : "border-[var(--border)] hover:border-[var(--ink)]")
            }
          >
            {k === "facebook" && <Facebook className="w-3 h-3" />}
            {k === "instagram" && <Instagram className="w-3 h-3" />}
            {k === "recent" && <Clock className="w-3 h-3" />}
            {k === "search" && <Search className="w-3 h-3" />}
            {label}
          </button>
        ))}
      </div>

      {platformFilter === "search" ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]" />
              <input
                data-testid="comment-search"
                value={commentQuery}
                onChange={(e) => setCommentQuery(e.target.value)}
                autoFocus
                placeholder={tr("Search comments, commenters, or post headlines…")}
                className="w-full pl-9 pr-8 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
              />
              {commentQuery && (
                <button
                  data-testid="comment-search-clear"
                  onClick={() => setCommentQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              data-testid="search-unanswered"
              onClick={() => setUnansweredOnly((v) => !v)}
              className={
                "px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-display border " +
                (unansweredOnly
                  ? "bg-[var(--slate-900)] text-white border-[var(--slate-900)]"
                  : "border-[var(--border)] hover:border-[var(--ink)]")
              }
            >{tr("Needs reply only")}</button>
          </div>
          <p className="text-[11px] text-[var(--ink-muted)]">{tr("Searches comments on the 25 most recent posts from each connected account. Meta’s API has no all-time comment search, so older threads need to be opened from the post list.")}</p>
          <RecentCommentsFeed
            data={searchQuery.data}
            isLoading={searchQuery.isLoading}
            query={commentQuery}
            unansweredOnly={unansweredOnly}
            emptyLabel={
              commentQuery
                ? tr("Nothing matches \"{commentQuery}\" in recent comments.", { commentQuery })
                : tr("No comments found on recent posts.")
            }
            onOpenAR={(payload) => setArTarget(payload)}
            onOpenPost={(post) => {
              setPlatformFilter("all");
              setSelectedPost(post);
            }}
            tz={tz}
          />
        </div>
      ) : platformFilter === "recent" ? (
        <RecentCommentsFeed
          data={recentQuery.data}
          isLoading={recentQuery.isLoading}
          onOpenAR={(payload) => setArTarget(payload)}
          onOpenPost={(post) => {
            setPlatformFilter("all");
            setSelectedPost(post);
          }}
          tz={tz}
        />
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,380px)_1fr] gap-6">
        {/* Post list — full width on mobile until a post is picked, then
            hidden so the comments pane replaces it. Always visible on
            desktop (`lg:`) as the left pane. */}
        <div
          className={
            "border border-[var(--border)] bg-white lg:max-h-[70vh] lg:overflow-y-auto " +
            (selectedPost ? "hidden lg:block" : "block")
          }
        >
          {/* Filter the post list itself. With 15 posts per connected
              account the left pane gets long fast, and finding "the
              marina story" by scrolling is the slowest part of the
              workflow. Sticky so it survives scrolling the list. */}
          <div className="sticky top-0 z-10 bg-white border-b border-[var(--border)] p-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]" />
              <input
                data-testid="post-search"
                value={postQuery}
                onChange={(e) => setPostQuery(e.target.value)}
                placeholder={tr("Find a post…")}
                className="w-full pl-7 pr-7 py-1 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
              />
              {postQuery && (
                <button
                  data-testid="post-search-clear"
                  onClick={() => setPostQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          {isLoading ? (
            <div className="p-6 text-sm text-[var(--ink-muted)]">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />{tr("Loading posts…")}</div>
          ) : filteredPosts.length === 0 ? (
            <div className="p-6 text-sm text-[var(--ink-muted)]">
              {postQuery ? (
                <>{tr("No posts match “{postQuery}”.", { postQuery })}</>
              ) : (
                <>{trRich("No posts to show. Connect a Facebook Page in <0>Settings</0>, then come back.", null, [<a href="/settings" className="underline" />])}</>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-[var(--border)]">
              {filteredPosts.map((p) => {
                const on = selectedPost?.post_id === p.post_id;
                return (
                  <li key={`${p.platform}-${p.post_id}`}>
                    <button
                      data-testid={`post-row-${p.post_id}`}
                      onClick={() => setSelectedPost(p)}
                      className={
                        "w-full text-left px-3 py-2.5 hover:bg-[var(--secondary)] " +
                        (on ? "bg-orange-50/60" : "")
                      }
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="flex items-center gap-1 text-[10px] font-mono-display text-[var(--ink-muted)] uppercase tracking-wider">
                          <PlatformIcon platform={p.platform} />
                          {p.handle}
                        </span>
                        <span
                          data-testid={`post-count-${p.post_id}`}
                          className={
                            "inline-flex items-center gap-1 px-1.5 py-0 text-[10px] font-mono-display " +
                            (p.comment_count > 0
                              ? "bg-orange-100 text-[var(--accent-hover)]"
                              : "bg-[var(--secondary)] text-[var(--ink-muted)]")
                          }
                        >
                          <MessageCircle className="w-2.5 h-2.5" />
                          {p.comment_count}
                        </span>
                      </div>
                      <div className="text-sm text-[var(--ink)] line-clamp-3">
                        {p.title}
                      </div>
                      <div className="text-[10px] text-[var(--ink-muted)] mt-0.5">
                        {formatInTz(p.created_time, tz, "compact")}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Comments pane — hidden on mobile until a post is picked
            (that's when the list slides out and this slides in). Always
            visible on desktop as the right pane. */}
        <div
          className={
            "border border-[var(--border)] bg-white p-4 lg:p-5 min-h-[400px] " +
            (selectedPost ? "block" : "hidden lg:block")
          }
        >
          {!selectedPost ? (
            <div className="text-sm text-[var(--ink-muted)] text-center py-16">{tr("Pick a post on the left to see its comments.")}</div>
          ) : (
            <>
              {/* Mobile-only "back to posts" affordance. On desktop the
                  post list is always visible on the left so this button
                  would be redundant. */}
              <button
                data-testid="back-to-posts"
                onClick={() => setSelectedPost(null)}
                className="lg:hidden mb-3 -ml-1 inline-flex items-center gap-1 px-2 py-1 text-xs uppercase tracking-wider text-[var(--ink-muted)] hover:text-[var(--ink)] font-mono-display"
              >
                <ChevronLeft className="w-3.5 h-3.5" />{tr("Back to posts")}</button>
              <div className="mb-4 pb-3 border-b border-[var(--border)]">
                <div className="flex items-center gap-2 mb-1">
                  <PlatformIcon platform={selectedPost.platform} />
                  {trRich("<0>{handle} · {comment_count} comment{s}</0>", { handle: selectedPost.handle, comment_count: selectedPost.comment_count, s: selectedPost.comment_count === 1 ? "" : "s" }, [<span className="overline" />])}
                  {selectedPost.permalink && (
                    <a
                      href={selectedPost.permalink}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-auto text-xs text-[var(--ink-muted)] hover:text-[var(--ink)] inline-flex items-center gap-1"
                    >{tr("Open on {s}", { s: selectedPost.platform === "facebook" ? tr("FB") : tr("IG") })}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <div className="text-sm text-[var(--ink)]">
                  {selectedPost.title}
                </div>
              </div>
              {commentsQuery.isLoading ? (
                <div className="text-sm text-[var(--ink-muted)]">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />{tr("Loading comments…")}</div>
              ) : (commentsQuery.data?.comments || []).length === 0 ? (
                <div className="text-sm text-[var(--ink-muted)] italic">{tr("No comments on this post yet.")}</div>
              ) : (
                <CommentThread
                  comments={commentsQuery.data.comments}
                  identity={commentsQuery.data.identity}
                  postCtx={selectedPost}
                  postText={selectedPost.title}
                  onOpenAR={(payload) => setArTarget(payload)}
                />
              )}
            </>
          )}
        </div>
      </div>
      )}

      {arTarget && (
        <ARDrawer target={arTarget} onClose={() => setArTarget(null)} tz={tz} />
      )}
      </div>
    </div>
  );
}
